// src/pages/PurchaseInvoiceDetail.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, X, Pencil, Trash2, FolderOpen } from 'lucide-react';
import toast from 'react-hot-toast';
import { purchaseInvoicesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatAmount, formatNumber } from '../utils/currency';
import './SaleInvoiceDetail.css';

const STATUTS = ['EN_ATTENTE', 'VALIDEE', 'PAYEE', 'ANNULEE'];
const TYPES = ['ACHAT', 'AVOIR_FOURNISSEUR'];

const TYPE_LABELS = {
  ACHAT: "Facture d'achat",
  AVOIR_FOURNISSEUR: 'Avoir fournisseur',
};

const statutColor = (s) => ({
  EN_ATTENTE: { color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
  VALIDEE:    { color: '#3B82F6', bg: 'rgba(59,130,246,0.12)' },
  PAYEE:      { color: '#16A34A', bg: 'rgba(34,197,94,0.12)' },
  ANNULEE:    { color: '#EF4444', bg: 'rgba(239,68,68,0.12)' },
}[s] || { color: '#8B8FA8', bg: 'rgba(139,143,168,0.12)' });

const toInput = (d) => (d ? String(d).slice(0, 10) : '');

function SLabel({ children }) {
  return <div className="sid-section-title">{children}</div>;
}
function Field({ label, value, children }) {
  return (
    <div className="sid-field-row">
      <div className="sid-field-label">{label}</div>
      <div className="sid-field-value">{children ?? (value || '—')}</div>
    </div>
  );
}
function EditField({ label, children }) {
  return (
    <div className="sid-field-row">
      <div className="sid-field-label">{label}</div>
      <div className="sid-field-value">{children}</div>
    </div>
  );
}

const EMPTY_FORM = {
  type_facture: 'ACHAT', ref_externe: '', date_facture: '', description: '',
  emetteur: '', destinataire: '', intervenants: '',
  montant_ht: '', taux_tva: '20', montant_ttc: '', montant_paye: '0',
  montant_ht_projet: '', statut: 'EN_ATTENTE',
};

export default function PurchaseInvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ['admin', 'super_admin', 'rh'].includes((user?.role || '').toLowerCase());

  const [invoice, setInvoice] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await purchaseInvoicesAPI.get(id);
      setInvoice(data);
      setForm({
        type_facture: data.type_facture || 'ACHAT',
        ref_externe: data.ref_externe || '',
        date_facture: toInput(data.date_facture),
        description: data.description || '',
        emetteur: data.emetteur || '',
        destinataire: data.destinataire || '',
        intervenants: data.intervenants || '',
        montant_ht: data.montant_ht ?? '',
        taux_tva: data.taux_tva ?? '20',
        montant_ttc: data.montant_ttc ?? '',
        montant_paye: data.montant_paye ?? '0',
        montant_ht_projet: data.montant_ht_projet ?? '',
        statut: data.statut || 'EN_ATTENTE',
      });
    } catch {
      toast.error("Facture d'achat introuvable");
      navigate('/purchase-invoices');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const computedTtc = Number(form.montant_ht || 0) * (1 + Number(form.taux_tva || 0) / 100);
  const impaye = computedTtc - Number(form.montant_paye || 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      await purchaseInvoicesAPI.update(id, {
        ...form,
        date_facture: form.date_facture || null,
        montant_ht: form.montant_ht !== '' ? parseFloat(form.montant_ht) : null,
        taux_tva: form.taux_tva !== '' ? parseFloat(form.taux_tva) : null,
        montant_paye: form.montant_paye !== '' ? parseFloat(form.montant_paye) : 0,
        montant_ht_projet: form.montant_ht_projet !== '' ? parseFloat(form.montant_ht_projet) : null,
        montant_ttc: undefined,
      });
      toast.success('Facture mise à jour');
      setEditMode(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer définitivement cette facture d\'achat ?')) return;
    try {
      await purchaseInvoicesAPI.remove(id);
      toast.success('Facture supprimée');
      navigate('/purchase-invoices');
    } catch {
      toast.error('Suppression impossible');
    }
  };

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', gap:12, color:'var(--text-muted)' }}>
      <div className="spinner" style={{ width:24, height:24 }}/> Chargement…
    </div>
  );

  const inv = invoice;
  const sc = statutColor(inv?.statut);

  return (
    <div className="sid-page">
      <div className="sid-topbar">
        <div className="sid-topbar-left">
          <button className="sid-back-btn" onClick={() => navigate('/purchase-invoices')}>
            <ArrowLeft size={16}/>
          </button>
          <div>
            <div className="sid-ref">{inv?.ref_externe || 'Sans référence'}</div>
            <div className="sid-ref-sub">{TYPE_LABELS[inv?.type_facture] || inv?.type_facture}</div>
          </div>
          <span className="sid-statut-badge" style={{ color: sc.color, background: sc.bg }}>
            {inv?.statut}
          </span>
        </div>

        <div className="sid-topbar-actions">
          {editMode ? (<>
            <button className="btn btn-secondary btn-sm" onClick={() => { setEditMode(false); load(); }}>
              <X size={13}/> Annuler
            </button>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
              <Save size={13}/> {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </>) : canEdit && (<>
            <button className="sid-action-btn sid-action-btn--danger" onClick={handleDelete}>
              <Trash2 size={13}/> Supprimer
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => setEditMode(true)}>
              <Pencil size={13}/> Modifier
            </button>
          </>)}
        </div>
      </div>

      <div className="card sid-card">
        <SLabel>EN-TÊTE</SLabel>
        <div className="sid-entete-grid">
          <div>
            {editMode ? (<>
              <EditField label="Type">
                <select className="form-control" value={form.type_facture}
                        onChange={e => setForm(f => ({ ...f, type_facture: e.target.value }))}>
                  {TYPES.map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
                </select>
              </EditField>
              <EditField label="Réf. externe">
                <input className="form-control" value={form.ref_externe}
                       onChange={e => setForm(f => ({ ...f, ref_externe: e.target.value }))}/>
              </EditField>
              <EditField label="Fournisseur">
                <input className="form-control" value={form.emetteur}
                       onChange={e => setForm(f => ({ ...f, emetteur: e.target.value }))}/>
              </EditField>
              <EditField label="Date">
                <input type="date" className="form-control" value={form.date_facture}
                       onChange={e => setForm(f => ({ ...f, date_facture: e.target.value }))}/>
              </EditField>
              <EditField label="Description">
                <input className="form-control" value={form.description}
                       onChange={e => setForm(f => ({ ...f, description: e.target.value }))}/>
              </EditField>
            </>) : (<>
              <Field label="Type" value={TYPE_LABELS[inv?.type_facture] || inv?.type_facture}/>
              <Field label="Réf. externe" value={inv?.ref_externe}/>
              <Field label="Fournisseur">
                <span style={{ fontWeight:600 }}>{inv?.emetteur || '—'}</span>
              </Field>
              <Field label="Date" value={inv?.date_facture ? new Date(inv.date_facture).toLocaleDateString('fr-FR') : '—'}/>
              <Field label="Description" value={inv?.description}/>
            </>)}
          </div>

          <div>
            {editMode ? (<>
              <EditField label="Statut">
                <select className="form-control" value={form.statut}
                        onChange={e => setForm(f => ({ ...f, statut: e.target.value }))}>
                  {STATUTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </EditField>
              <EditField label="Destinataire">
                <input className="form-control" value={form.destinataire}
                       onChange={e => setForm(f => ({ ...f, destinataire: e.target.value }))}/>
              </EditField>
              <EditField label="Intervenants">
                <input className="form-control" value={form.intervenants}
                       onChange={e => setForm(f => ({ ...f, intervenants: e.target.value }))}/>
              </EditField>
              <EditField label="Montant HT projet">
                <input type="number" className="form-control" value={form.montant_ht_projet}
                       onChange={e => setForm(f => ({ ...f, montant_ht_projet: e.target.value }))}/>
              </EditField>
            </>) : (<>
              <Field label="Statut">
                <span className="sid-statut-badge" style={{ color: sc.color, background: sc.bg }}>{inv?.statut}</span>
              </Field>
              <Field label="Destinataire" value={inv?.destinataire}/>
              <Field label="Intervenants" value={inv?.intervenants}/>
              <Field label="Projet">
                {inv?.projet_nom ? (
                  <button className="pd-link" onClick={() => navigate(`/projects/${inv.project_id}`)}
                          style={{ display:'inline-flex', alignItems:'center', gap:5 }}>
                    <FolderOpen size={13}/> {inv.projet_nom}
                  </button>
                ) : '—'}
              </Field>
              <Field label="Montant HT projet"
                     value={inv?.montant_ht_projet != null ? formatAmount(inv.montant_ht_projet) : '—'}/>
            </>)}
          </div>
        </div>
      </div>

      <div className="card sid-card">
        <SLabel>MONTANTS</SLabel>
        {editMode ? (
          <div className="sid-entete-grid">
            <div>
              <EditField label="Montant HT">
                <input type="number" className="form-control" value={form.montant_ht}
                       onChange={e => setForm(f => ({ ...f, montant_ht: e.target.value }))}/>
              </EditField>
              <EditField label="TVA %">
                <input type="number" className="form-control" value={form.taux_tva}
                       onChange={e => setForm(f => ({ ...f, taux_tva: e.target.value }))}/>
              </EditField>
            </div>
            <div>
              <EditField label="Montant TTC">
                <input className="form-control" value={formatNumber(computedTtc)} readOnly disabled
                       title="Calculé depuis le montant HT et le taux de TVA"/>
              </EditField>
              <EditField label="Montant payé">
                <input type="number" className="form-control" value={form.montant_paye}
                       onChange={e => setForm(f => ({ ...f, montant_paye: e.target.value }))}/>
              </EditField>
              <EditField label="Restant dû">
                <input className="form-control" value={formatNumber(impaye)} readOnly disabled/>
              </EditField>
            </div>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign:'right' }}>Montant HT</th>
                  <th style={{ textAlign:'right' }}>TVA</th>
                  <th style={{ textAlign:'right' }}>Montant TVA</th>
                  <th style={{ textAlign:'right' }}>Montant TTC</th>
                  <th style={{ textAlign:'right' }}>Payé</th>
                  <th style={{ textAlign:'right' }}>Restant dû</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ textAlign:'right' }}>{formatAmount(inv?.montant_ht)}</td>
                  <td style={{ textAlign:'right' }}>{formatNumber(inv?.taux_tva, 2)} %</td>
                  <td style={{ textAlign:'right' }}>
                    {formatAmount((inv?.montant_ttc || 0) - (inv?.montant_ht || 0))}
                  </td>
                  <td style={{ textAlign:'right', fontWeight:700 }}>{formatAmount(inv?.montant_ttc)}</td>
                  <td style={{ textAlign:'right' }}>{formatAmount(inv?.montant_paye)}</td>
                  <td style={{ textAlign:'right', fontWeight:600,
                               color: inv?.montant_impaye > 0 ? '#F59E0B' : '#16A34A' }}>
                    {formatAmount(inv?.montant_impaye)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
